import { useTranslation } from '@/lib/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  toast,
} from '@/components/ui';
import { friendlyMessage } from '@/lib/errors';
import type { MemberStatus } from '../hooks/useAdminUsersQuery';
import { useUserStatusMutation } from '../hooks/useUserStatusMutation';

interface UserStatusChangeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  targetStatus: MemberStatus;
  /** 휴면 회원을 활성으로 — 문구만 "휴면 해제"(API 는 정지 해제와 같은 status=ACTIVE). */
  dormancyRelease?: boolean;
  onSuccess?: () => void;
}

export const UserStatusChangeDialog = ({
  open,
  onOpenChange,
  userId,
  targetStatus,
  dormancyRelease = false,
  onSuccess,
}: UserStatusChangeDialogProps) => {
  const { t } = useTranslation('common');
  const mutation = useUserStatusMutation();

  const handleConfirm = async () => {
    try {
      await mutation.mutateAsync({ userId, payload: { status: targetStatus } });
    } catch (e) {
      // 종전엔 실패해도 아무 안내 없이 창만 남았다 — BE 사유(권한·상태 전이 불가 등)를 보인다.
      toast.error(friendlyMessage(e, '회원 상태 변경에 실패했습니다.'));
      return;
    }
    // 상세는 mutation 이 다시 읽어 휴면 해제일시가 갱신된다.
    if (dormancyRelease) toast.success(t('users.statusDialog.dormancyReleased', '휴면을 해제했습니다.'));
    onOpenChange(false);
    onSuccess?.();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !mutation.isPending && onOpenChange(o)}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {dormancyRelease ? t('users.actions.setStatus.DORMANT_RELEASE', '휴면 해제') : t('users.statusDialog.title')}
          </DialogTitle>
          <DialogDescription>
            {dormancyRelease
              ? t('users.statusDialog.desc.DORMANT_RELEASE')
              : t(`users.statusDialog.desc.${targetStatus}`)}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" size="sm" disabled={mutation.isPending}>
              {t('users.actions.cancel')}
            </Button>
          </DialogClose>
          <Button
            variant={targetStatus === 'SUSPENDED' ? 'danger' : 'primary'}
            size="sm"
            onClick={() => void handleConfirm()}
            disabled={mutation.isPending}
          >
            {t('users.actions.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
